import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, CheckCircle2 } from "lucide-react";

export default function Grievance() {
  const [officer, setOfficer] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", order_id: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    api.get("/grievance/officer").then((r) => setOfficer(r.data)).catch(() => setOfficer(null));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
      return toast.error("Name, email and description are required");
    }
    if (!form.email.includes("@")) return toast.error("Enter a valid email");
    if (form.description.trim().length < 20) {
      return toast.error("Please describe your complaint in at least 20 characters");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/grievances", form);
      setSubmitted(data);
      setForm({ name: "", email: "", phone: "", order_id: "", description: "" });
      toast.success(`Grievance filed. Ticket ${data.ticket_no}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit grievance");
    } finally {
      setBusy(false);
    }
  };

  if (!officer) return <div className="mx-auto max-w-3xl px-4 py-20 text-center">Loading…</div>;

  const ackHours = officer.acknowledgement_hours || 48;
  const resDays = officer.resolution_days || 30;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12" data-testid="grievance-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">CityShor Legal</div>
      <h1 className="font-display text-4xl mb-2">Grievance Redressal Mechanism</h1>
      <p className="text-sm text-gray-600 mb-8">
        In line with the Consumer Protection (E-Commerce) Rules, 2020, CityShor Pop-Up Bazaar has appointed a Grievance Officer to address concerns raised by buyers.
        Use the details below to reach the officer directly, or file a formal complaint through the form on this page.
      </p>

      <div className="rounded-2xl border border-[#F48E2E]/30 bg-[#FEF3E6] p-6 mb-8" data-testid="grievance-officer-card">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-2">Grievance Officer</div>
        <div className="font-display text-2xl" data-testid="officer-name">{officer.name}</div>
        {officer.designation && <div className="text-sm text-gray-600 mb-4">{officer.designation}</div>}
        <div className="grid gap-2 text-sm">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#F48E2E]" /> <a href={`mailto:${officer.email}`} data-testid="officer-email" className="hover:underline">{officer.email}</a></div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#F48E2E]" /> <a href={`tel:${officer.phone}`} data-testid="officer-phone" className="hover:underline">{officer.phone}</a></div>
          {officer.address && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#F48E2E] mt-0.5" /> <span data-testid="officer-address">{officer.address}</span></div>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-10">
        <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="ack-window">
          <div className="flex items-center gap-2 mb-2"><Clock className="h-4 w-4 text-[#F48E2E]" /><div className="text-xs uppercase tracking-widest text-gray-500">Acknowledgement</div></div>
          <div className="font-display text-2xl">Within {ackHours} hours</div>
          <div className="text-xs text-gray-500 mt-1">You will receive an acknowledgement from the Grievance Officer within {ackHours} hours of filing your complaint.</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid="res-window">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-[#F48E2E]" /><div className="text-xs uppercase tracking-widest text-gray-500">Resolution</div></div>
          <div className="font-display text-2xl">Within {resDays === 30 ? "1 month" : `${resDays} days`}</div>
          <div className="text-xs text-gray-500 mt-1">Every grievance will be resolved and closed within {resDays === 30 ? "one month" : `${resDays} days`} from the date it is received.</div>
        </div>
      </div>

      <h2 className="font-display text-2xl mb-4">File a complaint</h2>

      {submitted ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-6" data-testid="grievance-success">
          <div className="text-[11px] uppercase tracking-[0.2em] text-green-700 font-bold mb-1">Complaint received</div>
          <div className="font-display text-xl mb-2">Ticket {submitted.ticket_no}</div>
          <p className="text-sm text-green-900">
            You will receive an acknowledgement by {new Date(submitted.acknowledge_by).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.
            The full resolution is expected by {new Date(submitted.resolve_by).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
          <Button data-testid="grievance-file-another" onClick={() => setSubmitted(null)} variant="outline" className="rounded-full mt-4">File another complaint</Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4" data-testid="grievance-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Full Name <span className="text-red-500">*</span></Label>
              <Input data-testid="grv-name" value={form.name} onChange={upd("name")} />
            </div>
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input data-testid="grv-email" type="email" value={form.email} onChange={upd("email")} />
            </div>
            <div>
              <Label>Phone <span className="text-gray-400 font-normal">optional</span></Label>
              <Input data-testid="grv-phone" value={form.phone} onChange={upd("phone")} />
            </div>
            <div>
              <Label>Order ID <span className="text-gray-400 font-normal">optional</span></Label>
              <Input data-testid="grv-order" value={form.order_id} onChange={upd("order_id")} placeholder="If your complaint relates to a specific order" />
            </div>
          </div>
          <div>
            <Label>Describe your grievance <span className="text-red-500">*</span></Label>
            <Textarea data-testid="grv-desc" value={form.description} onChange={upd("description")} rows={6} placeholder="Please describe the issue, when it happened, and what outcome you expect." />
            <div className="text-[11px] text-gray-500 mt-1">Minimum 20 characters. Please avoid sharing card or password details.</div>
          </div>
          <Button data-testid="grv-submit" onClick={submit} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
            {busy ? "Submitting…" : "Submit grievance"}
          </Button>
        </div>
      )}

      <p className="text-[11px] text-gray-500 mt-8">
        If you are not satisfied with the resolution provided by the Grievance Officer, you may escalate the matter to the National Consumer Helpline (1915) or file a complaint on the e-Daakhil portal maintained by the Government of India.
      </p>
    </div>
  );
}
